import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function ExecutiveCoaching() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const focusAreas = [
    {
      icon: 'ri-user-star-line',
      title: 'Leadership Effectiveness',
      description: 'Strengthening leadership presence, communication, and decision-making to support effective executive leadership.'
    },
    {
      icon: 'ri-compass-line',
      title: 'Strategic Leadership',
      description: 'Helping leaders step back from day-to-day pressures to focus on long-term strategy, organizational priorities, and leadership impact.'
    },
    {
      icon: 'ri-arrow-up-circle-line',
      title: 'Leading Through Change',
      description: 'Supporting leaders as they navigate organizational transformation, team dynamics, and shifting priorities.'
    },
    {
      icon: 'ri-team-line',
      title: 'Leadership and Team Dynamics',
      description: 'Exploring how leaders influence culture, decision-making, and collaboration across leadership teams.'
    },
    {
      icon: 'ri-exchange-line',
      title: 'Executive Transition Support',
      description: 'Helping new or transitioning leaders establish clarity, confidence, and early momentum in their roles.'
    },
    {
      icon: 'ri-feedback-line',
      title: 'Leadership Assessments & 360 Reviews',
      description: 'Structured feedback processes that provide valuable insights into leadership strengths, blind spots, and opportunities for growth.'
    }
  ];

  const whoThisSupports = [
    'Leading organizations through periods of growth, transition, or change',
    'Stepping into new leadership roles or expanded responsibilities',
    'Navigating complex organizational dynamics or leadership team challenges',
    'Seeking to strengthen leadership effectiveness and strategic decision-making',
    'Preparing for greater leadership responsibility or organizational impact'
  ];

  const typicalEngagements = [
    'Executive leadership coaching for nonprofit CEOs and executive directors',
    'Leadership coaching for senior leadership team members',
    'Coaching support during leadership transitions',
    '360-degree leadership feedback and development processes',
    'Leadership effectiveness and organizational influence development'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#1a2b4a] via-[#1a2b4a] to-[#2d9b9b]/20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 border-4 border-white rounded-full -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 border-4 border-white rounded-full -ml-32 -mb-32"></div>
        </div>

        <div className="max-w-5xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-center">
            <span className="text-[#2d9b9b] text-sm font-semibold tracking-wide uppercase">Executive Coaching</span>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mt-6 mb-8 leading-tight" style={{ fontFamily: 'Crimson Text, serif' }}>
              Strategic Support for Mission-Driven Leaders
            </h1>
            <p className="text-xl text-white/90 leading-relaxed max-w-3xl mx-auto">
              Leadership in mission-driven organizations often involves navigating complexity, high expectations, and constant change. Executive coaching provides a confidential space for leaders to reflect, strengthen their leadership effectiveness, and develop the clarity needed to guide their organizations forward.
            </p>
          </div>
        </div>
      </section>

      {/* Who This Supports Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-[#1a2b4a] mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
              Who Executive Coaching Supports
            </h2>
            <p className="text-lg text-[#1a2b4a]/70 leading-relaxed mb-8">
              Executive coaching is designed for nonprofit executives and senior leaders who are:
            </p>
          </div>

          <div className="space-y-4">
            {whoThisSupports.map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-6 bg-[#f8f9fa] rounded-2xl hover:bg-[#2d9b9b]/5 transition-all duration-300">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-checkbox-circle-line text-2xl text-[#2d9b9b]"></i>
                </div>
                <p className="text-base text-[#1a2b4a] leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas of Focus Section */}
      <section className="py-20 bg-[#f8f9fa]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1a2b4a] mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
              Areas of Focus
            </h2>
            <p className="text-lg text-[#1a2b4a]/70 max-w-3xl mx-auto leading-relaxed">
              Coaching engagements are tailored to each leader's needs and organizational context, with focus areas that may include:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {focusAreas.map((area, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className={`${area.icon} text-5xl text-[#2d9b9b]`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#1a2b4a] mb-4">
                  {area.title}
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl font-bold text-[#1a2b4a] mb-8" style={{ fontFamily: 'Crimson Text, serif' }}>
            Approach
          </h2>
          
          <div className="space-y-6 text-lg text-[#1a2b4a]/70 leading-relaxed">
            <p>
              Jerilyn brings a unique perspective to executive coaching through her experience as a senior organizational leader. Having served as Chief Operating Officer, Chief People & Culture Officer, and Chief of Staff at Greenpeace USA, she understands the real challenges executives face when leading complex organizations and teams.
            </p>
            <p>
              Her coaching approach combines executive coaching, organizational development, and leadership advisory. She works with leaders as both a coach and strategic thought partner, helping them strengthen their leadership capacity while staying grounded in the realities of their organizations.
            </p>
            <p>
              Coaching engagements are tailored to each leader and may include regular coaching sessions, leadership assessments, 360-degree feedback processes, and ongoing advisory support.
            </p>
          </div>
        </div>
      </section>

      {/* Typical Engagements Section */}
      <section className="py-20 bg-[#f8f9fa]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl font-bold text-[#1a2b4a] mb-8" style={{ fontFamily: 'Crimson Text, serif' }}>
            Typical Coaching Engagements
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {typicalEngagements.map((engagement, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-all duration-300 border-l-4 border-[#2d9b9b]"
              >
                <p className="text-base text-[#1a2b4a] leading-relaxed font-medium">
                  {engagement}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-[#2d9b9b]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
            Connect
          </h2>
          <p className="text-xl text-white/90 leading-relaxed mb-10">
            If you are navigating a complex leadership moment and would benefit from a trusted thought partner, reach out to start a conversation.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <a
              href="mailto:jerilyn@jerilynemerson.com"
              className="inline-flex items-center gap-3 bg-white text-[#2d9b9b] px-8 py-4 rounded-full hover:bg-[#1a2b4a] hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line text-xl"></i>
              jerilyn@jerilynemerson.com
            </a>
            <a
              href="https://linkedin.com/in/jerilynemerson"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 border-2 border-white text-white px-8 py-4 rounded-full hover:bg-white hover:text-[#2d9b9b] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-linkedin-line text-xl"></i>
              LinkedIn
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}